import { useMemo, useRef, useState, useEffect, useCallback } from 'react';

interface RulerRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface RulerProps {
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  originX?: number;
  originY?: number;
  onOriginChange?: (ox: number, oy: number) => void;
  selectedRect?: RulerRect | null;
  children: React.ReactNode;
}

const RULER_SIZE = 22; // 固定高度/宽度，不随缩放变大
const RULER_BG = '#2a2a3c';
const TICK_COLOR = '#888';
const MAJOR_TICK_COLOR = '#bbb';
const LABEL_COLOR = '#ccc';
const BORDER_COLOR = '#444';

// 动态刻度步长：保证屏幕上刻度间距 >= 50px，避免文字拥挤
const STEPS = [10, 20, 50, 100, 200, 500, 1000];

export function pickStep(scale: number): number {
  for (const s of STEPS) {
    if (s * scale >= 50) return s;
  }
  return STEPS[STEPS.length - 1];
}

function TicksH({ width, scale, origin, viewport }: { width: number; scale: number; origin: number; viewport: number }) {
  const step = pickStep(scale);
  const majorStep = step * 5;
  const ticks = useMemo(() => {
    const arr: { pos: number; kind: 'minor' | 'major' }[] = [];
    for (let i = 0; i <= width; i += step) {
      arr.push({ pos: i, kind: i % majorStep === 0 ? 'major' : 'minor' });
    }
    return arr;
  }, [width, step, majorStep]);

  return (
    <>
      {ticks.map(({ pos, kind }) => {
        const x = (pos - origin) * scale;
        if (x < 0 || x > viewport) return null;
        const y2 = kind === 'major' ? 3 : RULER_SIZE - 5;
        const stroke = kind === 'major' ? MAJOR_TICK_COLOR : TICK_COLOR;
        return (
          <g key={pos}>
            <line x1={x} y1={RULER_SIZE} x2={x} y2={y2} stroke={stroke} strokeWidth={kind === 'major' ? 1 : 0.5} />
            {kind === 'major' && (
              <text x={x + 3} y={RULER_SIZE - 5} fill={LABEL_COLOR} fontSize={9} fontFamily="monospace" fontWeight={500}>
                {pos}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}

function TicksV({ height, scale, origin, viewport }: { height: number; scale: number; origin: number; viewport: number }) {
  const step = pickStep(scale);
  const majorStep = step * 5;
  const ticks = useMemo(() => {
    const arr: { pos: number; kind: 'minor' | 'major' }[] = [];
    for (let i = 0; i <= height; i += step) {
      arr.push({ pos: i, kind: i % majorStep === 0 ? 'major' : 'minor' });
    }
    return arr;
  }, [height, step, majorStep]);

  return (
    <>
      {ticks.map(({ pos, kind }) => {
        const y = (pos - origin) * scale;
        if (y < 0 || y > viewport) return null;
        const x2 = kind === 'major' ? 3 : RULER_SIZE - 5;
        const stroke = kind === 'major' ? MAJOR_TICK_COLOR : TICK_COLOR;
        return (
          <g key={pos}>
            <line x1={RULER_SIZE} y1={y} x2={x2} y2={y} stroke={stroke} strokeWidth={kind === 'major' ? 1 : 0.5} />
            {kind === 'major' && (
              <text x={RULER_SIZE - 4} y={y + 3} fill={LABEL_COLOR} fontSize={9} fontFamily="monospace" fontWeight={500} textAnchor="end">
                {pos}
              </text>
            )}
          </g>
        );
      })}
    </>
  );
}

/** 原点拖拽方块：拖拽调整标尺原点，双击重置回画布 (0,0) */
function OriginHandle({ originX, originY, onOriginChange }: { originX: number; originY: number; onOriginChange: (ox: number, oy: number) => void }) {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const ox0 = originX;
    const oy0 = originY;
    const move = (ev: MouseEvent) => {
      const s = (window as any).__scale || 1;
      onOriginChange(ox0 - (ev.clientX - startX) / s, oy0 - (ev.clientY - startY) / s);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onDoubleClick={() => onOriginChange(0, 0)}
      title="标尺原点（拖拽移动 / 双击重置）"
      style={{
        width: RULER_SIZE, height: RULER_SIZE, flexShrink: 0, cursor: 'crosshair',
        background: RULER_BG,
        borderRight: `1px solid ${BORDER_COLOR}`, borderBottom: `1px solid ${BORDER_COLOR}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{ color: MAJOR_TICK_COLOR, fontSize: 10 }}>┼</span>
    </div>
  );
}

export default function RulerWrap({ width, height, scaleX, scaleY, originX = 0, originY = 0, onOriginChange, selectedRect, children }: RulerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState({ w: 1200, h: 600 });
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setViewport({ w: rect.width, h: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 供 OriginHandle 读取当前缩放
  useEffect(() => {
    (window as any).__scale = scaleX;
  }, [scaleX]);

  const hasSelection = !!selectedRect;
  const sx = selectedRect ? selectedRect.x * scaleX : 0;
  const sy = selectedRect ? selectedRect.y * scaleY : 0;
  const ex = selectedRect ? (selectedRect.x + selectedRect.width) * scaleX : 0;
  const ey = selectedRect ? (selectedRect.y + selectedRect.height) * scaleY : 0;
  const selW = selectedRect ? selectedRect.width * scaleX : 0;
  const selH = selectedRect ? selectedRect.height * scaleY : 0;

  const handleContentMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = contentRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMousePos({
      x: (e.clientX - rect.left) / scaleX + originX,
      y: (e.clientY - rect.top) / scaleY + originY,
    });
  }, [scaleX, scaleY, originX, originY]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, userSelect: 'none' }}>
      {/* Row 1: 原点方块 + 顶部标尺 */}
      <div style={{ display: 'flex', flexShrink: 0 }}>
        <OriginHandle originX={originX} originY={originY} onOriginChange={onOriginChange || (() => {})} />
        <div style={{
          flex: 1,
          position: 'relative', height: RULER_SIZE, overflow: 'hidden',
          background: RULER_BG, borderBottom: `1px solid ${BORDER_COLOR}`,
        }}>
          {/* svg 宽度 = 画布宽度（width*scaleX），刻度与画布严格对齐；溢出容器部分被 overflow:hidden 裁剪，
              保证普通/全屏模式下刻度尺显示范围一致（画满整个画布） */}
          <svg width={Math.max(width * scaleX, viewport.w)} height={RULER_SIZE} style={{ display: 'block' }}>
            <TicksH width={width} scale={scaleX} origin={originX} viewport={width * scaleX} />
            {mousePos && (
              <line x1={mousePos.x * scaleX} y1={0} x2={mousePos.x * scaleX} y2={RULER_SIZE} stroke="#40a9ff" strokeWidth={1} opacity={0.8} />
            )}
            {hasSelection && selectedRect && (
              <>
                <rect x={sx} y={0} width={selW} height={RULER_SIZE} fill="rgba(24,144,255,0.15)" stroke="#40a9ff" strokeWidth={1} />
                <line x1={sx} y1={0} x2={sx} y2={RULER_SIZE} stroke="#40a9ff" strokeWidth={1.5} />
                <line x1={ex} y1={0} x2={ex} y2={RULER_SIZE} stroke="#40a9ff" strokeWidth={1.5} />
                <rect x={sx + 2} y={1} width={28} height={13} rx={3} fill="#40a9ff" />
                <text x={sx + 5} y={11} fill="#fff" fontSize={9} fontFamily="monospace" fontWeight={700}>
                  {Math.round(selectedRect.x)}
                </text>
              </>
            )}
          </svg>
        </div>
      </div>

      {/* Row 2: 左侧标尺 + 内容 */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0 }}>
        <div style={{
          position: 'relative', width: RULER_SIZE, flexShrink: 0, overflow: 'hidden',
          background: RULER_BG, borderRight: `1px solid ${BORDER_COLOR}`,
        }}>
          <svg width={RULER_SIZE} height={Math.max(height * scaleY, viewport.h)} style={{ display: 'block' }}>
            <TicksV height={height} scale={scaleY} origin={originY} viewport={height * scaleY} />
            {mousePos && (
              <line x1={0} y1={mousePos.y * scaleY} x2={RULER_SIZE} y2={mousePos.y * scaleY} stroke="#40a9ff" strokeWidth={1} opacity={0.8} />
            )}
            {hasSelection && selectedRect && (
              <>
                <rect x={0} y={sy} width={RULER_SIZE} height={selH} fill="rgba(24,144,255,0.15)" stroke="#40a9ff" strokeWidth={1} />
                <line x1={RULER_SIZE} y1={sy} x2={0} y2={sy} stroke="#40a9ff" strokeWidth={1.5} />
                <line x1={RULER_SIZE} y1={ey} x2={0} y2={ey} stroke="#40a9ff" strokeWidth={1.5} />
                <rect x={2} y={sy + 2} width={18} height={13} rx={3} fill="#40a9ff" />
                <text x={5} y={sy + 12} fill="#fff" fontSize={9} fontFamily="monospace" fontWeight={700}>
                  {Math.round(selectedRect.y)}
                </text>
              </>
            )}
          </svg>
        </div>

        {/* Content area (canvas) - 自适应父容器，不固定尺寸。
            overflow:hidden 只裁剪绘制不裁剪 pointer-events，fill 溢出时组件会穿透到右侧属性面板；
            用 clip-path: inset(0) 同时裁剪绘制与命中测试，杜绝交互穿越 */}
        <div ref={contentRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', clipPath: 'inset(0)', minHeight: 0, minWidth: 0 }} onMouseMove={handleContentMouseMove} onMouseLeave={() => setMousePos(null)}>
          {children}
          {mousePos && (
            <div style={{ position: 'absolute', left: mousePos.x * scaleX + 8, top: mousePos.y * scaleY + 8, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 3, zIndex: 10001, pointerEvents: 'none', fontFamily: 'monospace' }}>
              ({Math.round(mousePos.x)}, {Math.round(mousePos.y)})
            </div>
          )}

          {hasSelection && selectedRect && (
            <>
              <div style={{
                position: 'absolute', left: sx, top: 0,
                width: 1, height: '100%',
                background: 'repeating-linear-gradient(to bottom, #40a9ff 0px, #40a9ff 4px, rgba(64,169,255,0) 4px, rgba(64,169,255,0) 8px)',
                zIndex: 9998, pointerEvents: 'none', opacity: 0.5,
              }} />
              <div style={{
                position: 'absolute', top: sy, left: 0,
                height: 1, width: '100%',
                background: 'repeating-linear-gradient(to right, #40a9ff 0px, #40a9ff 4px, rgba(64,169,255,0) 4px, rgba(64,169,255,0) 8px)',
                zIndex: 9998, pointerEvents: 'none', opacity: 0.5,
              }} />
              <div style={{
                position: 'absolute', left: sx - 6, top: sy - 6,
                width: selW + 12, height: selH + 12,
                border: '1.5px solid #40a9ff', zIndex: 9997, pointerEvents: 'none', boxSizing: 'border-box',
              }} />
              <div style={{
                position: 'absolute', left: sx + 6, top: sy - 20,
                background: '#40a9ff', color: '#fff', fontSize: 10,
                padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap',
                zIndex: 10000, pointerEvents: 'none', fontFamily: 'monospace',
                fontWeight: 700, lineHeight: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
              }}>
                ({Math.round(selectedRect.x)}, {Math.round(selectedRect.y)})
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
